import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import mixpanelClient from '@disco/api/mixpanel'
import { updateBriefInPanes } from '@disco/state-management/actions/ExplorerActions'
import {
  duplicateBrief,
  saveBrief,
} from '@disco/state-management/reducers/entities/briefs'
import { getConstanceSettings } from '@disco/state-management/selectors/user'
import type { Brief } from '@disco/types/types/brief'
import dayjs from '@disco/utils/dayjs'
import { pushUINotification } from '@disco/utils/notifications'

import { HoverContext } from 'components/ui/hover-provider'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { useReopenBrief } from '../../hooks/use-reopen-brief'

type UseBriefMenuOptions = {
  brief: Brief | null | undefined
  editMode?: boolean
  hoverContext?: React.ContextType<typeof HoverContext>
  onOpenChange?: (open: boolean) => void
}

/**
 * Brief menu state and handlers, shared by the three-dot menu (BriefActionMenu)
 * and the row's right-click menu (BriefContextMenu) so the two can't drift apart.
 */
export const useBriefMenu = ({
  brief,
  editMode,
  hoverContext,
  onOpenChange,
}: UseBriefMenuOptions) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const constanceSettings = useSelector(getConstanceSettings)
  const [closing, setClosing] = useState(false)
  const [redirectToShareModal, setRedirectToShareModal] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const navigateToShareModal = () => {
    if (!brief) return
    if (editMode) {
      navigate(`/brief-editor-v2/${brief.id}/share`)
    } else {
      navigate(`/briefs/${brief.id}/share`)
    }
    setRedirectToShareModal(false)
  }

  const {
    reopening,
    isDeadlineInPast,
    handleReopenBrief,
    deadlinePickerModal,
  } = useReopenBrief({
    brief: brief ?? null,
    onSuccess: updatedBrief => {
      if (!brief) return
      const initialDeadline = dayjs(brief.deadline)
      const newDeadline = dayjs(updatedBrief.deadline)
      const number_days_extended = initialDeadline.diff(newDeadline, 'day')
      mixpanelClient.sendPureEvent('Brief reopened', {
        Context: 'Briefs',
        brief_id: updatedBrief.id,
        deadline_extended: updatedBrief.deadline,
        days_extended: number_days_extended,
      })
      dispatch(updateBriefInPanes(updatedBrief))
      if (redirectToShareModal) {
        navigateToShareModal()
        setRedirectToShareModal(false)
      }
    },
    onCancel: () => {
      setRedirectToShareModal(false)
    },
  })

  useEffect(() => {
    // Keep hover state true as dropdown menu is using portal
    if (menuOpen && hoverContext?.setHover) {
      hoverContext.setHover(true)
    }
  }, [menuOpen, hoverContext])

  const briefWorkflowV2Enabled = constanceSettings?.BRIEF_WORKFLOW_V2 || false
  const canShareBrief = brief?.publishing_status !== 'draft'

  const handleEditBrief = () => {
    if (!brief?.id) return
    const url = briefWorkflowV2Enabled
      ? `/brief-editor-v2/${brief.id}`
      : `/brief-editor/${brief.id}/content`
    navigate(url)
  }

  const handleDuplicate = async () => {
    if (!brief) return
    const newBrief = await dispatch(duplicateBrief(brief.id)).unwrap()
    mixpanelClient.sendPureEvent('Brief duplicated', {
      Context: 'Briefs',
      original_brief_id: brief.id,
    })
    const editUrl = briefWorkflowV2Enabled
      ? `/brief-editor-v2/${newBrief.id}`
      : `/brief-editor/${newBrief.id}/content`
    navigate(editUrl)
  }

  const handleShare = () => {
    if (isDeadlineInPast) {
      // If brief is closed and deadline is in the past, require reopening before sharing
      setRedirectToShareModal(true)
      handleReopenBrief()
      return
    }
    navigateToShareModal()
  }

  const handleManageBriefAccess = () => {
    if (!brief) return
    if (editMode) {
      navigate(`/brief-editor-v2/${brief.id}/access`)
    } else {
      navigate(`/briefs/${brief.id}/access`)
    }
  }

  const handleBriefSettings = () => {
    if (!brief) return
    if (editMode) {
      navigate(`/brief-editor-v2/${brief.id}/settings`)
    } else {
      navigate(`/briefs/${brief.id}/settings`)
    }
  }

  const handleDelete = () => {
    if (!brief?.id) return
    if (editMode) {
      navigate(`/brief-editor-v2/${brief.id}/delete`)
      return
    }
    navigate(`/briefs/${brief.id}/delete`)
  }

  const handleCloseBrief = async () => {
    if (!brief) return
    setClosing(true)
    try {
      const updatedBrief = await dispatch(
        saveBrief({ ...brief, submission_status: 'closed' }),
      ).unwrap()

      dispatch(updateBriefInPanes(updatedBrief))
      const briefSentAt = dayjs(brief.sentAt)
      const today = dayjs(new Date())
      const daysOpen = briefSentAt.diff(today, 'day')
      mixpanelClient.sendPureEvent('Brief closed', {
        Context: 'Briefs',
        brief_id: brief.id,
        submission_count: brief.response_count,
        days_open: daysOpen,
      })
      pushUINotification({
        message: 'Brief has been closed successfully',
        level: 'success',
      })
    } catch (_error) {
      pushUINotification({
        message: 'Failed to close brief',
        level: 'error',
      })
    } finally {
      setClosing(false)
    }
  }

  const handleOpenChange = (open: boolean) => {
    setMenuOpen(open)
    if (hoverContext?.setHover && !open) {
      hoverContext.setHover(false)
    }
    onOpenChange?.(open)
  }

  return {
    briefWorkflowV2Enabled,
    canShareBrief,
    closing,
    reopening,
    deadlinePickerModal,
    handleOpenChange,
    handleShare,
    handleEditBrief,
    handleDuplicate,
    handleManageBriefAccess,
    handleBriefSettings,
    handleReopenBrief,
    handleCloseBrief,
    handleDelete,
  }
}
